export const ROLEPLAY_METADATA_KEYS = [
  'persona_key',
  'starting_expression',
  'realtime_voice',
  'max_active_seconds',
  'max_learner_turns',
  'learning_objectives',
  'actor_prompt',
  'win_condition_prompt',
  'scorecard_categories',
] as const;

export type RoleplayMetadataKey = typeof ROLEPLAY_METADATA_KEYS[number];

/**
 * Icon names the backend accepts for a scorecard category (lucide-react
 * component names). Kept local to the admin editor so authoring does not
 * depend on the learner-facing icon registry.
 */
export const ROLEPLAY_SCORECARD_ICON_NAMES = [
  'Target',
  'Heart',
  'HandHeart',
  'Ear',
  'MessageSquareText',
  'MessageCircleQuestion',
  'Wind',
  'Flag',
  'Handshake',
  'Lightbulb',
  'Shield',
  'Compass',
  'Scale',
  'Sparkles',
  'Users',
  'Clock',
  'ListChecks',
  'Smile',
  'Brain',
] as const;

export type RoleplayScorecardIconName = typeof ROLEPLAY_SCORECARD_ICON_NAMES[number];

export const DEFAULT_ROLEPLAY_SCORECARD_ICON: RoleplayScorecardIconName = 'Target';
export const ROLEPLAY_SCORECARD_MIN_CATEGORIES = 2;
export const ROLEPLAY_SCORECARD_MAX_CATEGORIES = 6;
export const ROLEPLAY_SCORECARD_LABEL_MAX_LENGTH = 60;
export const ROLEPLAY_SCORECARD_DESCRIPTION_MAX_LENGTH = 300;

export type ScorecardCategoryDraft = {
  /** Stable server-generated slug. Blank for rows the server has not keyed yet. */
  key: string;
  label: string;
  description: string;
  /** Blank means "use the server default icon". */
  icon: string;
};

export type RoleplayMetadataFormValues = {
  personaKey: string;
  startingExpression: string;
  realtimeVoice: string;
  maxActiveSeconds: string;
  maxLearnerTurns: string;
  learningObjectives: string[];
  actorPrompt: string;
  winConditionPrompt: string;
  scorecardCategories: ScorecardCategoryDraft[];
};

const stringValue = (value: unknown, fallback = '') =>
  typeof value === 'string' ? value : fallback;

const numberInputValue = (value: unknown, fallback: number) => {
  if (value === undefined) return String(fallback);
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return value.trim();
  return '';
};

const stringArrayValue = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : [];

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const scorecardCategoriesValue = (value: unknown): ScorecardCategoryDraft[] =>
  Array.isArray(value)
    ? value.filter(isPlainRecord).map((category) => ({
      key: stringValue(category.key),
      label: stringValue(category.label),
      description: stringValue(category.description),
      icon: stringValue(category.icon),
    }))
    : [];

export const createEmptyScorecardCategory = (): ScorecardCategoryDraft => ({
  key: '',
  label: '',
  description: '',
  icon: DEFAULT_ROLEPLAY_SCORECARD_ICON,
});

/**
 * Returns an author-facing hint when the draft categories would be rejected by
 * the backend, or null when they are empty (no scorecard) or complete.
 */
export const scorecardCategoriesValidationMessage = (
  categories: ScorecardCategoryDraft[],
): string | null => {
  if (categories.length === 0) return null;
  if (categories.length < ROLEPLAY_SCORECARD_MIN_CATEGORIES) {
    return `Add at least ${ROLEPLAY_SCORECARD_MIN_CATEGORIES} categories, or remove this one to turn the scorecard off.`;
  }
  if (categories.length > ROLEPLAY_SCORECARD_MAX_CATEGORIES) {
    return `Use at most ${ROLEPLAY_SCORECARD_MAX_CATEGORIES} categories.`;
  }
  const incomplete = categories.findIndex(
    (category) => !category.label.trim() || !category.description.trim(),
  );
  if (incomplete >= 0) {
    return `Category ${incomplete + 1} needs a label and a "what good looks like" description.`;
  }
  return null;
};

export const roleplayMetadataToFormValues = (
  metadata: Record<string, unknown> | undefined,
  defaultPersonaKey = '',
): RoleplayMetadataFormValues => ({
  personaKey: stringValue(metadata?.persona_key, defaultPersonaKey),
  startingExpression: stringValue(metadata?.starting_expression, 'at_ease'),
  realtimeVoice: stringValue(metadata?.realtime_voice, 'marin'),
  maxActiveSeconds: numberInputValue(metadata?.max_active_seconds, 600),
  maxLearnerTurns: numberInputValue(metadata?.max_learner_turns, 12),
  learningObjectives: stringArrayValue(metadata?.learning_objectives),
  actorPrompt: stringValue(metadata?.actor_prompt),
  winConditionPrompt: stringValue(metadata?.win_condition_prompt),
  scorecardCategories: scorecardCategoriesValue(metadata?.scorecard_categories),
});

const parsedIntegerOrBlank = (value: string): number | '' => {
  const normalized = value.trim();
  return normalized ? Number(normalized) : '';
};

const scorecardCategoryToMetadata = (
  category: ScorecardCategoryDraft,
): Record<string, string> => {
  const row: Record<string, string> = {};
  // The key links a learner's scores across attempts: keep an existing one
  // verbatim and omit it for new rows so the server generates it.
  const key = category.key.trim();
  if (key) row.key = key;
  row.label = category.label.trim();
  row.description = category.description.trim();
  const icon = category.icon.trim();
  if (icon) row.icon = icon;
  return row;
};

/**
 * Structured form values as metadata. `scorecard_categories` is omitted when
 * the form has no categories because the key is optional and the backend
 * normalizes an empty list away.
 */
export const roleplayFormValuesToMetadata = (
  values: RoleplayMetadataFormValues,
): Partial<Record<RoleplayMetadataKey, unknown>> => {
  const metadata: Partial<Record<RoleplayMetadataKey, unknown>> = {
    persona_key: values.personaKey,
    starting_expression: values.startingExpression,
    realtime_voice: values.realtimeVoice,
    max_active_seconds: parsedIntegerOrBlank(values.maxActiveSeconds),
    max_learner_turns: parsedIntegerOrBlank(values.maxLearnerTurns),
    learning_objectives: values.learningObjectives,
    actor_prompt: values.actorPrompt,
    win_condition_prompt: values.winConditionPrompt,
  };
  if (values.scorecardCategories.length > 0) {
    metadata.scorecard_categories = values.scorecardCategories.map(scorecardCategoryToMetadata);
  }
  return metadata;
};

/**
 * Applies only the keys owned by the structured roleplay form. Raw metadata is
 * the source of truth for every other key, including future or organization-
 * specific extensions that this client does not understand. Owned keys the
 * form leaves empty (currently only `scorecard_categories`) are removed so
 * clearing them in the form clears them on save.
 */
export const mergeRoleplayFormValuesIntoMetadata = (
  rawMetadata: Record<string, unknown> | undefined,
  formValues: RoleplayMetadataFormValues,
): Record<string, unknown> => {
  const formMetadata = roleplayFormValuesToMetadata(formValues);
  const merged: Record<string, unknown> = { ...(rawMetadata ?? {}), ...formMetadata };
  if (!('scorecard_categories' in formMetadata)) {
    delete merged.scorecard_categories;
  }
  return merged;
};
